import { useState } from 'react';
import { Check, ChevronsUpDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { FIELD_TRIGGER } from '@/modules/social-agent/ui-chrome';

export interface ComboboxOption {
	value: string;
	label: string;
	description?: string;
}

interface PreconditionComboboxProps {
	id?: string;
	value: string;
	onChange: (value: string) => void;
	options: ComboboxOption[];
	placeholder?: string;
	searchPlaceholder?: string;
	emptyText?: string;
	disabled?: boolean;
}

/**
 * A searchable single-value combobox restricted to the given options — used for tool names
 * and field dot-paths, where the options are the account's real tools or a real tool's
 * output schema. Deliberately no free-text escape hatch: a typo'd field path or a tool name
 * that doesn't actually exist would silently never resolve at runtime (the evaluator fails
 * closed rather than erroring), so only what's actually real is selectable here.
 */
export function PreconditionCombobox({
	id,
	value,
	onChange,
	options,
	placeholder = 'Select...',
	searchPlaceholder = 'Search...',
	emptyText = 'No matches',
	disabled
}: PreconditionComboboxProps) {
	const [open, setOpen] = useState(false);
	const [search, setSearch] = useState('');

	const selected = options.find((o) => o.value === value);

	const select = (next: string) => {
		onChange(next);
		setSearch('');
		setOpen(false);
	};

	return (
		<Popover open={open} onOpenChange={setOpen}>
			<PopoverTrigger asChild>
				<Button
					id={id}
					variant='outline'
					role='combobox'
					aria-expanded={open}
					disabled={disabled}
					className={cn('h-10 w-full justify-between bg-white px-3 text-xs font-normal', FIELD_TRIGGER)}
				>
					<span className={cn('truncate', !value && 'text-muted-foreground')}>
						{value ? (selected?.label ?? value) : placeholder}
					</span>
					<ChevronsUpDown className='ml-2 size-3.5 shrink-0 opacity-50' />
				</Button>
			</PopoverTrigger>
			<PopoverContent
				className='w-[var(--radix-popover-trigger-width)] p-0 rounded-xl overflow-hidden'
				align='start'
			>
				<Command>
					<CommandInput
						placeholder={searchPlaceholder}
						value={search}
						onValueChange={setSearch}
						className='text-xs'
					/>
					<CommandList
						className='max-h-[260px] overflow-y-auto overscroll-behavior-y-contain'
						// This popover is portaled from inside a modal Dialog. Radix's scroll-lock on
						// <body> (react-remove-scroll) swallows native wheel-to-scroll on a separately
						// portaled list like this one — it's genuinely scrollable (scrollTop works),
						// the browser just never gets to apply the wheel delta. Do it by hand, same fix
						// as MultiSelect's CommandList.
						onWheel={(e) => {
							e.currentTarget.scrollTop += e.deltaY;
						}}
					>
						{!options.length ? (
							<div className='py-4 px-3 text-xs text-center text-muted-foreground'>{emptyText}</div>
						) : (
							<CommandEmpty className='py-4 text-xs text-center'>No matches for "{search}"</CommandEmpty>
						)}
						<CommandGroup>
							{options.map((option) => (
								<CommandItem
									key={option.value}
									value={option.value}
									onSelect={() => select(option.value)}
									className='text-xs flex flex-col items-start gap-0'
								>
									<div className='flex items-center gap-2 w-full'>
										<Check className={cn('size-3.5 shrink-0', value === option.value ? 'opacity-100' : 'opacity-0')} />
										<span className='truncate'>{option.value}</span>
									</div>
									{option.description && (
										<span className='pl-[22px] text-[11px] text-muted-foreground truncate w-full'>
											{option.description}
										</span>
									)}
								</CommandItem>
							))}
						</CommandGroup>
					</CommandList>
				</Command>
			</PopoverContent>
		</Popover>
	);
}
